import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import useCart from "store";
import Api from "utils/wApi/index";
import Cookie from "js-cookie";
import dayjs from "dayjs";

// components
import Layout from "layout/index";
import {
	Box,
	Container,
	Stack,
	Typography,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Button,
	TextField,
} from "@mui/material";
import { BottomButtons } from "components/unitComponent";
import { SvgIconStyle } from "components";
import AlertDialog from "components/pay/alert-dialog";
import PayStep1 from "components/pay/pay-step1";
import PayStep21 from "components/pay/pay-step2-1";
import PayStep22 from "components/pay/pay-step2-2";

// translations
import { useTranslation } from "react-i18next";

import ApplePayIcon from "assets/icons/apple-pay.svg";
import SamsungPayIcon from "assets/icons/samsung-pay.svg";
import NaverPayIcon from "assets/icons/naverpay-primary.svg";
import KakaoPayIcon from "assets/icons/kakaopay.svg";
import AliPayIcon from "assets/icons/alipay-primary.svg";
import WeChatPayIcon from "assets/icons/wechat-primary.svg";
import CardIcon from "assets/icons/card.svg";
import QrcodeIcon from "assets/icons/qrcode.svg";
import BarcodeIcon from "assets/icons/barcode.svg";
import TossIcon from "assets/image/toss_icon.png";
import TossFaceIcon from "assets/image/toss_facepay.png";
import CardInfoUrl from "assets/image/pay-card-info.png";
import QrInfoUrl from "assets/image/pay-qr-info.png";

const PAYMENT_CALLBACK_TIMEOUT_MS = 60000;
const CASH_PAYMENT_PASSWORD = "7353";

export default function Pay() {
	const Kiosk_ID = Cookie.get("Kiosk_ID");
	const [status, setStatus] = useState("PAY"); // 1.SELECT 2-1.PAY 2-2.SIMPLE_PAY
	const [catId, setCatId] = useState();
	const { festival, total } = useCart((state) => state);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { orderId } = location.state;
	const [payType, setPayType] = useState(""); // UNIFIED | CARD | BARCODE
	const payTypeRef = useRef("");
	const paymentInProgressRef = useRef(false);
	const paymentTimeoutRef = useRef(null);

	const [barcodeType, setBarcodeType] = useState(""); // "ALIPAY_OFFLINE" | "WECHAT_PAY_OFFLINE"

	const [alertWaiting, setAlertWaiting] = useState(false);
	const [alertTitle, setAlertTitle] = useState("");
	const [alertIcon, setAlertIcon] = useState();

	// 결제실패 여부
	const [payState, setPayState] = useState(false);
	const [payStateDesc, setPayStateDesc] = useState("");
	const [cashPasswordOpen, setCashPasswordOpen] = useState(false);
	const [cashPassword, setCashPassword] = useState("");
	const [cashPasswordError, setCashPasswordError] = useState("");
	const [cashPaymentPending, setCashPaymentPending] = useState(false);

	// 키오스크 외 기기로 결제 시도
	const payNotKiosk = () => {
		setPayState(true);
		setPayStateDesc(`키오스크로 결제해주세요.`);
	};

	const clearPaymentTimeout = () => {
		if (paymentTimeoutRef.current) {
			clearTimeout(paymentTimeoutRef.current);
			paymentTimeoutRef.current = null;
		}
	};

	const resetPaymentProgress = () => {
		paymentInProgressRef.current = false;
		clearPaymentTimeout();
	};

	const startPaymentTimeout = () => {
		resetPaymentProgress();
		paymentInProgressRef.current = true;
		paymentTimeoutRef.current = setTimeout(() => {
			resetPaymentProgress();
			payTypeRef.current = "";
			setPayType("");
			setBarcodeType("");
			setStatus("PAY");
			setPayState(true);
			setPayStateDesc("결제 응답이 지연되고 있습니다. 다시 시도해 주세요.");
		}, PAYMENT_CALLBACK_TIMEOUT_MS);
	};

	const resolvePayMethod = (payload) => {
		const paymentType = `${payload?.PAYMENTTYPE || ""}`.toUpperCase();
		const easyPayCode = `${payload?.EASYPAYCODE || ""}`.toUpperCase();
		const transactionText = `${payload?.TRANSACTIONTEXT || ""}`.toUpperCase();
		const selectedPayType = `${payTypeRef.current || ""}`.toUpperCase();

		if (
			selectedPayType === "NPAY_POINT" ||
			paymentType === "NPAY_POINT" ||
			paymentType === "NPAY" ||
			paymentType === "NAVER_POINT" ||
			(easyPayCode === "NHQ" && transactionText === "PRO")
		) {
			return "NAVER_POINT";
		}
		if (
			selectedPayType === "TOSS" ||
			selectedPayType === "TOSS_POINT" ||
			paymentType === "TOSS" ||
			paymentType === "TOSS_POINT" ||
			(easyPayCode === "TSM" && transactionText === "PRO")
		) {
			return "TOSS";
		}

		return "CARD";
	};

	// 카드결제 | 간편결제
	const handlePay = (type, bcType) => {
		if (paymentInProgressRef.current) {
			console.warn("결제가 이미 진행 중입니다.");
			return;
		}

		if (window.ChromeAPI === undefined) {
			payNotKiosk();
		} else {
			startPaymentTimeout();

			if (bcType) {
				setBarcodeType(bcType);
			}

			payTypeRef.current = type;
			setPayType(type); // UI용 상태 변경

			// 콜백이 너무 빠르게 들어오면 ref로 보완
			setTimeout(() => {
				try {
					window.ChromeAPI.toPayment(`${total}`, type);
				} catch (error) {
					resetPaymentProgress();
					setPayState(true);
					setPayStateDesc(`결제 호출 실패 :: ${error.message}`);
				}
			}, 5);
		}
	};

	useEffect(() => {
		return () => {
			resetPaymentProgress();
		};
	}, []);

	// 2. 키오스크로부터의 콜백
	useEffect(() => {
		window.toPaymentResult = function (res) {
			console.log("res", res);

			let status, payload;

			if (Array.isArray(res)) {
				// 이미 JS 배열로 넘어온 경우
				[status, payload] = res;
			} else {
				// 문자열로 넘어온 경우 (대부분 CARD 모드)
				[status, payload] = JSON.parse(res);
			}

			console.log("// 2.결제 callback", status, payload);
			clearPaymentTimeout();

			if (status !== "PaymentSuccess") {
				// 실패
				resetPaymentProgress();
				setPayState(true);
				setPayStateDesc(
					`onPay :: ${payload?.message || "결제에 실패했습니다."}`
				);
			} else {
				if (payTypeRef.current === "BARCODE") {
					const barcode = payload?.CUSTBARCODE || "";
					const catId = payload?.CATID || "";
					setSimplePayComplete({ orderId, barcode, catId }, barcodeType);
				} else if (
					payTypeRef.current === "UNIFIED" ||
					payTypeRef.current === "CARD" ||
					payTypeRef.current === "NPAY_POINT" ||
					payTypeRef.current === "NPAY_FACE" ||
					payTypeRef.current === "TOSS_POINT" ||
					payTypeRef.current === "TOSS_FACE"
				) {
					setPaymentComplete(payload);
				} else {
					console.warn("결제방식이 선택되지 않았습니다:");
					setPayState(true);
					setPayStateDesc(
						"결제 타입이 정상적으로 설정되지 않았습니다. 다시 시도해 주세요."
					);

					// 상태 초기화
					resetPaymentProgress();
					payTypeRef.current = "";
					setPayType("");
					setBarcodeType("");
					setStatus("PAY"); //초기값인 "PAY"로 복구
					window.alert(`결제 실패 :: 결제 방식 설정 실패`);
				}
			}
		};
	}, [barcodeType, orderId]);

	// 간편결제 처리
	const setSimplePayComplete = async (
		{ orderId, barcode, catId },
		barcodeType
	) => {
		try {
			const { data } = await Api.simplePay({ orderId, barcode, catId }, null, {
				params: { method: barcodeType },
			});

			console.log("간편결제 성공:", data);

			const orderData = {
				title: festival.name,
				date: dayjs().format("YYYY-MM-DD HH:mm:ss"),
				price: data.amount.toLocaleString(),
				phone: data.phone || "",
				items: JSON.stringify(
					data.items.map((item) => ({
						name: item.name,
						amount: `${item.qty}`,
						price: `${(item.price / item.qty).toLocaleString()}`,
						total: `${item.price.toLocaleString()}`,
					}))
				),
			};
			navigate("/complete", { state: { orderData } });
		} catch (error) {
			resetPaymentProgress();
			console.error("간편결제 실패:", error);
			setPayState(true);
			setPayStateDesc(
				`간편결제 실패: ${error.response?.data?.message || error.message}`
			);
			window.alert(
				`간편결제 실패 :  ${error.response?.data?.message || error.message}`
			);
		}
	};

	const openCashPassword = () => {
		if (paymentInProgressRef.current) {
			return;
		}
		setCashPassword("");
		setCashPasswordError("");
		setCashPasswordOpen(true);
	};

	const closeCashPassword = () => {
		if (paymentInProgressRef.current) {
			return;
		}
		setCashPasswordOpen(false);
		setCashPassword("");
		setCashPasswordError("");
	};

	const handleCashPayment = async () => {
		if (paymentInProgressRef.current || !cashPasswordOpen) {
			return;
		}
		if (cashPassword !== CASH_PAYMENT_PASSWORD) {
			setCashPassword("");
			setCashPasswordError("비밀번호가 올바르지 않습니다. 다시 입력해 주세요.");
			return;
		}

		paymentInProgressRef.current = true;
		setCashPaymentPending(true);
		setCashPassword("");
		try {
			const { data } = await Api.completeCash({ orderId });
			const cashTid = `CASH-${orderId}`;
			const orderData = {
				title: festival.name,
				date: dayjs().format("YYYY-MM-DD HH:mm:ss"),
				price: `${Number(data.amount ?? total).toLocaleString()}`,
				appNo: cashTid + ` (${Cookie.get("Kiosk_ID")})`,
				items: JSON.stringify(
					(data.items || []).map((item) => ({
						name: item.name,
						amount: `${item.qty}`,
						price: `${(item.price / item.qty).toLocaleString()}`,
						total: `${item.price.toLocaleString()}`,
					}))
				),
			};
			navigate("/complete", { state: { orderData } });
		} catch (error) {
			setCashPasswordOpen(false);
			setPayState(true);
			setPayStateDesc(
				`현금결제 처리 실패 :: ${
					error.response?.data?.message || error.message
				}`
			);
		} finally {
			setCashPaymentPending(false);
			resetPaymentProgress();
		}
	};

	// 3.결제 결과 & 후 처리
	async function setPaymentComplete(data) {
		const INIT = {
			ERROR_CHECK_RESULT: "S", // (정상:'S', 에러:'E')
			MSGTYPE: "1010",
			TID: "8800009923", // 단말기번호
			MSGNO: "000000000001", // POS번호 + 거래 일련번호
			SWVERSION: "1100210001", // S/W 버전
			HWMODELNO: "TU25040092", // H/W 시리얼번호
			READERNO: "###NPS-700QN1002JTTPAYDAEMON2113", // KTC인증번호 데몬 + 리더기
			TRANSTIME: "250711154445", // 전문 전송 일자 “YYMMHHDDMMSS”
			FILLER: "     ", // 다중 VAN구분자(“MV”:나이스페이먼츠, “SV”: 기타 VAN)
			PKTYPE: "S", // 페킷타입(정상:'S', 에러:'E')
			REPLY: "0000", // 0000 : 정상, 그 외 에러코드
			MESSAGE: "00433869    250711154445519215433837", // 응답코드 승인:승인번호(12)+승인일시(yymmddhhmmss)+VAN고유번호(12) | 거절:오류 메시지
			MERCHANTNO: "120748414      ", // 가맹점 번호
			ISSUERCD: "0800", // 발급사 코드
			ISSUER: "현대카드            ", // 발급사 명
			ACQUIERCD: "0800", // 매입사 코드
			ACQUIER: "현대카드            ", // 매입사 명
			CARDKBN: "CK", // 카드구분 'CK':신용, 'CH':체크, 'GK':기프트카드, 'UP':중국은련, 'NT':BC/NH면세유, 'OL':유가보조카드, 'CP':원카드
			ACQUIERKBN: " ", // 매입구분
			EMV_FCDO: " ", // EMV 데이터 옵션
			PRINTOPT: "N", // 카드사 전표출력
			DCAMOUNT: "         ", // 할인금액
			EVENTPOINT: "         ", // 발생포인트
			USEPOINT: "         ", // 가용포인트
			TOTPOINT: "         ", // 누적포인트
			GIFTBALANCE: "         ", // 기프트 잔액
			CARDPOINT: "         ", // 카드사 포인트
			POINTINFO: "                                                            ", // 포인트 정보
			ADDINFO1: "                ", // 부가정보1
			ADDINFO2:
				"                                                                ",
			ADDINFO3:
				"                                                                                                                                ",
			TRACK2DATA: "94901900*********   ", // 전표 카드번호 출력 데이터
			PRINTMSG1: "                            ", // 프린트 메시지
			PRINTMSG2: "                            ",
			PRINTMSG3: "                            ",
			PRINTMSG4: "                            ",
			PRINTMSG5: "                            ",
			PRINTMSG6: "                            ",
			EMV_REPLY: "", // EMV 응답데이터
			CR: "\r",
		};
		// const [result, cardNum, cardName, quot, tid, date] = data.split("&");
		// const formattedDate = date.replace(
		// 	/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/,
		// 	"20$1-$2-$3 $4:$5:$6"
		// );
		const {
			TRACK2DATA: cardNo,
			ISSUER: cardName,
			TRANSTIME,
			TID: legacyTid,
			CATID,
		} = data;
		const formattedDate = dayjs(TRANSTIME).format("YYYY-MM-DD HH:mm:ss");
		const payMethod = resolvePayMethod(data);

		const message = String(data?.MESSAGE || "");
		// TPAY는 승인번호(12자리, 공백 포함) 뒤에 승인일시와 VAN 번호가 붙습니다.
		// NICE는 MESSAGE 전체가 승인번호이므로 그대로 사용합니다.
		const approvalNo = message.slice(0, 12).trim();
		const terminalId = String(CATID || legacyTid || "").trim();
		if (!terminalId || !approvalNo) {
			resetPaymentProgress();
			setPayState(true);
			setPayStateDesc("단말기번호 또는 승인번호가 없습니다.");
			return;
		}
		const requestTid = `${terminalId}-${approvalNo}`;

		try {
			const { data } = await Api.complete({
				orderId,
				tid: requestTid,
				payMethod,
			});

			const orderData = {
				title: festival.name,
				date: formattedDate,
				price: `${(+data.amount).toLocaleString()}`,
				cardName: cardName,
				cardNo: cardNo,
				appNo: approvalNo + ` (${Cookie.get("Kiosk_ID")})`,
				items: JSON.stringify(
					data.items.map((item) => {
						return {
							name: item.name,
							amount: `${item.qty}`, //수량
							price: `${+(item.price / item.qty).toLocaleString()}`, //단가
							total: `${+item.price.toLocaleString()}`, //가격
						};
					})
				),
				// items: JSON.stringify(
				//   cart.flatMap((item) => {
				//     const nameTime = item.orderTime
				//       ? fDate(item.orderTime, "HH:mm") + ` - `
				//       : "";
				//     const names = item.description.split(" - ");

				//     if (names.length > 1) {
				//       const [name, ...subNames] = names;
				//       return [
				//         { name: `${nameTime}${name}` },
				//         ...subNames.map((subName) => ({
				//           name: `ㄴ(옵션)${subName}`,
				//           amount: `${item.buyCount}`,
				//           price: `${+(
				//             item.paymentPrice / item.buyCount
				//           ).toLocaleString()}`,
				//           total: `${+item.paymentPrice.toLocaleString()}`,
				//         })),
				//       ];
				//     }
				//     return [
				//       {
				//         name: `${nameTime}${item.description}`,
				//         amount: `${item.buyCount}`,
				//         price: `${+(
				//           item.paymentPrice / item.buyCount
				//         ).toLocaleString()}`,
				//         total: `${+item.paymentPrice.toLocaleString()}`,
				//       },
				//     ];
				//   })
				// ),
			};
			navigate("/complete", {
				state: { orderData },
			});
		} catch (error) {
			resetPaymentProgress();
			console.log(`결제api-error::${error.message}`);
			setPayState(true);
			setPayStateDesc(
				`카드결제 실패 :: ${error.response?.data?.message || error.message}`
			);
			window.alert(
				`카드결제 실패 :  ${error.response?.data?.message || error.message}`
			);
		}
		setStatus("SELECT");
	}

	return (
		<Layout>
			{payType === "" ? (
				<Container
					sx={{
						pt: 4.5,
						px: "56px !important",
						display: "flex",
						flexDirection: "column",
						gap: 2,
						height: "calc(100% - 161px)",
					}}
				>
					<Typography variant="h2">{t("payment")}</Typography>
					<Typography variant="subtitle2" color={"grey.200"}>
						{t("select-method")}
					</Typography>

					{/* ------------- 결제수단 선택 ------------- */}
					<Button
						fullWidth
						variant="outlined"
						sx={{
							flexDirection: "column",
							gap: 2,
							py: 5,
							borderColor: "black",
							color: "black",
							"&:hover,&.active": {
								borderColor: "black",
							},
						}}
						onClick={() => handlePay("CARD")}
					>
						<SvgIconStyle src={CardIcon} sx={{ width: 40, height: 30 }} />
						<Typography variant="h2">{t("card")}</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<SvgIconStyle src={SamsungPayIcon} origin sx={{ width: 110 }} />
							<SvgIconStyle src={ApplePayIcon} origin sx={{ width: 50 }} />
						</Box>
					</Button>
					<Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
						<Button
							fullWidth
							variant="outlined"
							sx={{
								flexDirection: "column",
								gap: 2,
								py: 5,
								borderColor: "black",
								color: "black",
								"&:hover,&.active": {
									borderColor: "black",
								},
							}}
							onClick={() => handlePay("BARCODE", "ALI")}
						>
							<SvgIconStyle
								src={AliPayIcon}
								origin
								sx={{ width: 120, height: 30 }}
							/>
						</Button>
						<Button
							fullWidth
							variant="outlined"
							sx={{
								flexDirection: "column",
								gap: 2,
								py: 5,
								borderColor: "black",
								color: "black",
								"&:hover,&.active": {
									borderColor: "black",
								},
							}}
							onClick={() => handlePay("BARCODE", "H5")}
						>
							<SvgIconStyle
								src={WeChatPayIcon}
								origin
								sx={{ width: 170, height: 30 }}
							/>
						</Button>
					</Box>
					{/* {Kiosk_ID === "T-POS" || Kiosk_ID === "K-POS" ? (
						<Button
							fullWidth
							variant="contained"
							sx={{
								flexDirection: "column",
								gap: 2,
								py: 5,
							}}
							onClick={openCashPassword}
						>
							<SvgIconStyle src={CardIcon} sx={{ width: 40, height: 30 }} />
							<Typography variant="h2">현금결제</Typography>
							<Typography>현금을 받은 후 눌러주세요.</Typography>
						</Button>
					) : null} */}
				</Container>
			) : payType === "CARD" ? (
				<Box
					sx={{
						pt: 4.5,
						px: "56px !important",
						display: "flex",
						flexDirection: "column",
						gap: 2,
						height: "calc(100% - 161px)",
					}}
				>
					<Typography variant="h2">{t("card")}</Typography>
					<Typography variant="subtitle2" color={"grey.200"}>
						{t("insert-card")}
					</Typography>

					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							width: "100%",
							mt: "130px",
							mb: "60px",
						}}
					>
						<img
							src={CardInfoUrl}
							alt="Insert your card with the chip side up."
							width={410}
						/>
					</Box>
					<Typography
						variant="h3"
						textAlign="center"
						mt={3}
						whiteSpace={"break-spaces"}
					>
						{t("insert-ic")}
					</Typography>
				</Box>
			) : payType === "UNIFIED" || payType === "NPAY_FACE" ? (
				<Box
					sx={{
						pt: 4.5,
						px: "56px !important",
						display: "flex",
						flexDirection: "column",
						gap: 2,
						height: "calc(100% - 161px)",
					}}
				>
					<Typography variant="h2">통합결제</Typography>
					<Typography variant="subtitle2" color={"grey.200"}>
						결제 단말기에서 결제 방법을 선택해 주세요.
					</Typography>

					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 2,
							width: "100%",
							mt: "180px",
							mb: "100px",
						}}
					>
						<SvgIconStyle
							origin
							src={TossIcon}
							sx={{ width: 80, height: 80 }}
						/>
						<SvgIconStyle
							origin
							src={TossFaceIcon}
							sx={{ width: 70, height: 70 }}
						/>
					</Box>
					<Typography
						variant="h3"
						textAlign="center"
						mt={3}
						whiteSpace={"break-spaces"}
					>
						{`결제 단말기에서 결제가 진행 중 입니다.`}
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						pt: 4.5,
						px: "56px !important",
						display: "flex",
						flexDirection: "column",
						gap: 2,
						height: "calc(100% - 161px)",
					}}
				>
					<Typography variant="h2">{t("simple-pay")}</Typography>
					<Typography variant="subtitle2" color={"grey.200"}>
						{t("scan-code")}
					</Typography>

					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							width: "100%",
							mt: "130px",
							mb: "60px",
						}}
					>
						<img
							src={QrInfoUrl}
							alt="Insert your card with the chip side up."
							width={420}
						/>
					</Box>
					<Typography
						variant="h3"
						textAlign="center"
						mt={3}
						whiteSpace={"break-spaces"}
					>
						{`QR코드 / 바코드를 단말기 오른쪽 스캐너에 인식시켜 주세요. \n\n Scan your QR code or barcode\nat the scanner on the terminal’s right side.`}
					</Typography>
				</Box>
			)}
			{/* bottom =============================================== */}
			<Box>
				<Stack
					direction="row"
					justifyContent="space-between"
					sx={{ bgcolor: "grey.soft", p: 3 }}
				>
					<Typography variant="h3">{t("total-pay")}</Typography>
					{/*  */}
					<Typography variant="h3" color="primary.main">
						{t("price", { price: Number(total).toLocaleString() })}
					</Typography>
				</Stack>
				<BottomButtons
					right={{
						label: t("cancel"),
						onClick: () => {
							navigate(-1);
						},
					}}
				/>
			</Box>

			<Dialog
				open={cashPasswordOpen}
				onClose={closeCashPassword}
				fullWidth
				maxWidth="xs"
				aria-labelledby="cash-password-title"
			>
				<DialogTitle id="cash-password-title">현금결제 비밀번호</DialogTitle>
				<DialogContent>
					<Typography sx={{ mb: 3 }}>
						현금을 받은 후 비밀번호 4자리를 입력해 주세요.
					</Typography>
					<TextField
						autoFocus
						fullWidth
						label="비밀번호"
						type="password"
						value={cashPassword}
						disabled={cashPaymentPending}
						autoComplete="off"
						inputProps={{ inputMode: "none", maxLength: 4 }}
						error={Boolean(cashPasswordError)}
						helperText={cashPasswordError}
						onChange={(event) => {
							setCashPassword(
								event.target.value.replace(/\D/g, "").slice(0, 4)
							);
							setCashPasswordError("");
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								handleCashPayment();
							}
						}}
					/>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(3, 1fr)",
							gap: 1,
							mt: 2,
						}}
					>
						{[
							"1",
							"2",
							"3",
							"4",
							"5",
							"6",
							"7",
							"8",
							"9",
							"전체 지우기",
							"0",
							"지우기",
						].map((key) => (
							<Button
								key={key}
								variant="outlined"
								disabled={cashPaymentPending}
								sx={{ minHeight: 64, fontSize: key.length === 1 ? 28 : 16 }}
								onClick={() => {
									setCashPasswordError("");
									setCashPassword((value) =>
										key === "전체 지우기"
											? ""
											: key === "지우기"
											? value.slice(0, -1)
											: (value + key).slice(0, 4)
									);
								}}
							>
								{key}
							</Button>
						))}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						fullWidth
						disabled={cashPaymentPending}
						onClick={closeCashPassword}
					>
						취소
					</Button>
					<Button
						fullWidth
						variant="contained"
						disabled={cashPaymentPending || cashPassword.length !== 4}
						onClick={handleCashPayment}
					>
						{cashPaymentPending ? "처리 중..." : "결제 완료"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* 결제대기 안내모달 */}
			<Dialog
				open={payState}
				onClose={() => {
					setPayState(false);
				}}
			>
				<DialogContent sx={{ pt: 5, textAlign: "center", width: 400 }}>
					<Typography variant="h3">{t("alert")}</Typography>
					<Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
						{payStateDesc}
					</Typography>
				</DialogContent>
				<Divider />

				<DialogActions
					onClick={() => {
						setPayState(false);
					}}
				>
					<Box sx={{ textAlign: "center", width: "100%", cursor: "pointer" }}>
						<Typography variant="body1">{t("check")}</Typography>
					</Box>
				</DialogActions>
			</Dialog>
		</Layout>
	);
}
